import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { FileText, Upload, Eye, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadDocument, deleteDocument, API_URL } from '../../lib/api';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*';

/**
 * "Devis client" file block — shown in the Informations tab.
 * - Lists all uploaded documents of type "devis" attached to the dossier
 * - Lets the user upload a new devis (creates a new document, type=devis)
 * - View / delete buttons per file
 *
 * Storage strategy: re-uses the existing /documents/upload endpoint
 * with type_document=devis so the file also appears in the Documents tab.
 */
export const DevisFileBlock = ({ dossier, onUploaded }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const devisDocs = (dossier?.documents || []).filter((d) => d.type_document === 'devis');

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 10 Mo)');
            return;
        }
        setUploading(true);
        try {
            await uploadDocument(dossier.id, 'devis', file, '');
            toast.success('Devis téléversé');
            onUploaded && (await onUploaded());
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('Supprimer ce devis ?')) return;
        try {
            await deleteDocument(docId);
            toast.success('Devis supprimé');
            onUploaded && (await onUploaded());
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const downloadUrl = (docId) => `${API_URL}/api/documents/${docId}/download`;

    return (
        <div className="border border-[#E4E4E7] rounded-sm p-3 bg-[#FAFAFA]" data-testid="devis-file-block">
            <div className="flex items-center justify-between mb-2">
                <Label className="field-label flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Devis client ({devisDocs.length})
                </Label>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="rounded-sm"
                    data-testid="upload-devis-btn"
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Upload...
                        </>
                    ) : (
                        <>
                            <Upload className="w-4 h-4 mr-1" /> {devisDocs.length ? 'Ajouter une version' : 'Téléverser le devis'}
                        </>
                    )}
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="devis-file-input"
                />
            </div>
            {devisDocs.length === 0 ? (
                <p className="text-xs text-[#52525B] italic">Aucun devis client téléversé pour ce dossier</p>
            ) : (
                <ul className="divide-y divide-[#E4E4E7] bg-white border border-[#E4E4E7] rounded-sm">
                    {devisDocs.map((d) => (
                        <li key={d.id} className="flex items-center gap-2 p-2 text-sm" data-testid={`devis-row-${d.id}`}>
                            <FileText className="w-4 h-4 text-[#0284C7] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{d.filename}</p>
                                <p className="text-[11px] text-[#A1A1AA]">
                                    {d.size ? `${(d.size / 1024).toFixed(0)} Ko` : ''}
                                    {d.uploaded_at ? ` · ${new Date(d.uploaded_at).toLocaleDateString('fr-FR')}` : ''}
                                </p>
                            </div>
                            <a
                                href={downloadUrl(d.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-8 w-8 inline-flex items-center justify-center text-[#0284C7] hover:bg-[#0284C7]/10 rounded-sm"
                                title="Voir / télécharger"
                                data-testid={`devis-view-${d.id}`}
                            >
                                <Eye className="w-4 h-4" />
                            </a>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(d.id)}
                                className="h-8 w-8 text-[#DC2626]"
                                title="Supprimer"
                                data-testid={`devis-delete-${d.id}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
